import * as THREE from "three";
import { GLOW_ATTRIBUTE, seeded } from "./facet";

// A low-poly water sheet over any outline (spike 002 C4; decision 0048: "a stream and a pond, low-poly with an
// animated surface"): a jittered grid over the outline's box, kept where it reaches the shore plus a margin (the bank
// hides the edge), each face coloured by the water's depth there (shallow to deep) with a lighter rim near the shore,
// and a `shore` attribute (metres inside the shore, 0 outside) that a water material reads to damp its waves at the
// bank and draw its foam. The outline, surface and floor come from the game (the same numbers its water queries use:
// @rpg/collision's water shapes).

/** The per-vertex distance inside the shore (m, ≥ 0). */
export const SHORE_ATTRIBUTE = "shore";

export interface WaterSurfaceOptions {
  /** The box to cover (x, z). */
  min: { x: number; z: number };
  max: { x: number; z: number };
  /** Grid cell (m). */
  cell: number;
  /** How far inside the outline a point is (m; negative outside). */
  distance(x: number, z: number): number;
  /** The water's level over (x, z), defined everywhere near the outline (a stream's nearest reach). */
  level(x: number, z: number): number;
  /** The ground under the water (for the depth tint); default: 1 m under the level everywhere. */
  floor?(x: number, z: number): number;
  colours: { shallow: THREE.Color; deep: THREE.Color; edge: THREE.Color };
  /** The depth (m) at which the colour is fully deep. */
  deepAt: number;
  /** The width (m) of the lighter rim inside the shore. */
  edgeWidth: number;
  /** How far past the shore the sheet runs (m), under the bank. */
  margin: number;
  /** Grid jitter as a share of the cell (0..0.45); default 0.3. */
  jitter?: number;
  seed?: number;
}

/** The water sheet: non-indexed, flat (normals up; the material facets its waves), colour, glow 0 and shore. */
export function waterSurface(o: WaterSurfaceOptions): THREE.BufferGeometry {
  const nx = Math.max(1, Math.ceil((o.max.x - o.min.x) / o.cell));
  const nz = Math.max(1, Math.ceil((o.max.z - o.min.z) / o.cell));
  const rng = seeded(o.seed ?? 1);
  const j = Math.min(0.45, Math.max(0, o.jitter ?? 0.3)) * o.cell;
  const vx: number[] = [];
  const vz: number[] = [];
  const vd: number[] = [];
  for (let iz = 0; iz <= nz; iz++)
    for (let ix = 0; ix <= nx; ix++) {
      const edge = ix === 0 || iz === 0 || ix === nx || iz === nz;
      const x = o.min.x + ((o.max.x - o.min.x) * ix) / nx + (edge ? 0 : (rng() * 2 - 1) * j);
      const z = o.min.z + ((o.max.z - o.min.z) * iz) / nz + (edge ? 0 : (rng() * 2 - 1) * j);
      vx.push(x);
      vz.push(z);
      vd.push(o.distance(x, z));
    }
  const pos: number[] = [];
  const col: number[] = [];
  const shore: number[] = [];
  const c = new THREE.Color();
  const tri = (a: number, b: number, d: number): void => {
    if (Math.max(vd[a]!, vd[b]!, vd[d]!) < -o.margin) return;
    const cx = (vx[a]! + vx[b]! + vx[d]!) / 3;
    const cz = (vz[a]! + vz[b]! + vz[d]!) / 3;
    const lv = o.level(cx, cz);
    const depth = o.floor ? Math.max(0, lv - o.floor(cx, cz)) : 1;
    const dist = (vd[a]! + vd[b]! + vd[d]!) / 3;
    c.copy(o.colours.shallow).lerp(o.colours.deep, Math.min(1, depth / Math.max(1e-6, o.deepAt)));
    if (dist < o.edgeWidth) c.lerp(o.colours.edge, 0.4 * (1 - Math.max(0, dist) / Math.max(1e-6, o.edgeWidth)));
    for (const k of [a, b, d]) {
      pos.push(vx[k]!, o.level(vx[k]!, vz[k]!), vz[k]!);
      col.push(c.r, c.g, c.b);
      shore.push(Math.max(0, vd[k]!));
    }
  };
  const id = (ix: number, iz: number): number => iz * (nx + 1) + ix;
  for (let iz = 0; iz < nz; iz++)
    for (let ix = 0; ix < nx; ix++) {
      // alternate the diagonal so the facets don't line up in rows
      if ((ix + iz) % 2 === 0) {
        tri(id(ix, iz), id(ix, iz + 1), id(ix + 1, iz + 1));
        tri(id(ix, iz), id(ix + 1, iz + 1), id(ix + 1, iz));
      } else {
        tri(id(ix, iz), id(ix, iz + 1), id(ix + 1, iz));
        tri(id(ix + 1, iz), id(ix, iz + 1), id(ix + 1, iz + 1));
      }
    }
  const g = new THREE.BufferGeometry();
  const n = pos.length / 3;
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(new Float32Array(n * 3).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
  g.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute(GLOW_ATTRIBUTE, new THREE.Float32BufferAttribute(new Float32Array(n), 1));
  g.setAttribute(SHORE_ATTRIBUTE, new THREE.Float32BufferAttribute(shore, 1));
  g.computeBoundingSphere();
  return g;
}
